import React from 'react';
import { useTranslation } from 'react-i18next';

const ScoreLimiterSkill_Mobile = ({ isLimiterActive, onToggleLimiter, budget, onBudgetChange, insufficientPoints, currentSpent, isOverBudget, onDeactivateAll, onToggleAllFastLearner, onCleanSkills }) => {
    const { t } = useTranslation();
    return (
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-6">
            <div className="flex flex-col space-y-4">
                {/* Row 1: Title */}
                <div className="flex items-center gap-3 border-b border-gray-100 pb-3">
                    <div className="p-2 bg-purple-100 rounded-xl text-purple-600">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-gray-800">{t('ScoreLimiterSkill.title')}</h3>
                        <p className="text-xs text-gray-500">{t('ScoreLimiterSkill.subtitle')}</p>
                    </div>
                </div>

                {/* Row 2: Options */}
                <div className="flex justify-center">
                    <button
                        onClick={onToggleAllFastLearner}
                        className="w-full py-2 bg-gradient-to-b from-orange-300 to-orange-500 text-white text-sm font-bold uppercase tracking-wider rounded-lg shadow-md hover:shadow-lg hover:brightness-110 transition-all active:scale-95 border border-orange-600"
                    >
                        ⚡︎ Fast Learner ⚡︎
                    </button>
                </div>

                {/* Row 3: Remaining / Total */}
                <div className={`flex flex-col items-center justify-center bg-gray-50 rounded-lg p-3 transition-all duration-300 ${isLimiterActive ? 'opacity-100' : 'opacity-50 pointer-events-none'}`}>
                    <div className="text-xs text-gray-500 font-medium mb-1 uppercase tracking-wide">{t('ScoreLimiterSkill.label_remaining_total')}</div>
                    <div className="flex items-center gap-3">
                        <div className={`font-mono text-2xl font-bold ${budget - currentSpent < 0 ? 'text-red-500' : 'text-green-600'}`}>
                            {budget - currentSpent}
                        </div>
                        <span className="text-gray-300 text-xl">/</span>
                        <div className="relative">
                            <input
                                type="number"
                                value={budget === 0 ? '' : budget}
                                onChange={(e) => onBudgetChange(parseInt(e.target.value) || 0)}
                                className="w-24 pl-3 pr-8 py-1 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:ring-0 font-mono text-center font-bold text-gray-700 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                placeholder="0"
                                min="0"
                            />
                            <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs font-medium">Pt</span>
                        </div>
                    </div>
                </div>

                {/* Row 4: Activate/Deactivate */}
                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <span className="text-sm font-medium text-gray-600">{t('ScoreLimiterSkill.switch_activate')}</span>
                    <div className="flex items-center gap-4">
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={isLimiterActive}
                                onChange={onToggleLimiter}
                                className="sr-only peer"
                            />
                            <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-purple-600"></div>
                        </label>

                        <button
                            onClick={onCleanSkills}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors border border-gray-200"
                            title={t('ScoreLimiterSkill.tooltip_clean')}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>

                        <button
                            onClick={onDeactivateAll}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors border border-gray-200"
                            title={t('ScoreLimiterSkill.tooltip_deactivate_all')}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Warning Message */}
            {isLimiterActive && insufficientPoints && (
                <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg flex items-center gap-2 text-red-600 animate-fade-in-up text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span className="font-medium">{t('ScoreLimiterSkill.warning_insufficient')}</span>
                </div>
            )}

            {/* Over Budget Warning */}
            {isOverBudget && (
                <div className="mt-4 p-3 bg-orange-50 border border-orange-100 rounded-lg flex items-center gap-2 text-orange-700 animate-fade-in-up text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span className="font-medium">
                        {t('ScoreLimiterSkill.error_budget_exceeded_mobile', { current: currentSpent, total: budget })}
                    </span>
                </div>
            )}
        </div>
    );
};

export default ScoreLimiterSkill_Mobile;
// Revisado: 2025-12-18 [I18n Scan]
